import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { API_BASE_URL as API_BASE } from '../../config/api';
import './AdminMembershipManagePage.css';

const AdminMembershipManagePage = () => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  
  // Annual Discount State
  const [annualDiscount, setAnnualDiscount] = useState({
    value: '40',
    isVisible: true
  });
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountFormValue, setDiscountFormValue] = useState('');
  const [savingDiscount, setSavingDiscount] = useState(false);

  useEffect(() => {
    fetchPlans();
    fetchMembershipSettings();
  }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest('.amp-plan-card-menu')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  const fetchMembershipSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/membership/admin/settings/annualDiscount`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAnnualDiscount({
          value: data.value || '40',
          isVisible: data.isVisible !== false
        });
      }
    } catch (error) {
      console.error('Error fetching membership settings:', error);
    }
  };

  const handleOpenDiscountModal = () => {
    setDiscountFormValue(annualDiscount.value);
    setShowDiscountModal(true);
  };

  const handleSaveDiscount = async () => {
    try {
      setSavingDiscount(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/membership/admin/settings/annualDiscount`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: discountFormValue,
          isVisible: annualDiscount.isVisible,
          description: 'Annual billing discount percentage'
        })
      });
      
      if (response.ok) {
        setAnnualDiscount(prev => ({ ...prev, value: discountFormValue }));
        setShowDiscountModal(false);
        alert('Annual discount saved successfully!');
      } else {
        alert('Failed to save annual discount');
      }
    } catch (error) {
      console.error('Error saving annual discount:', error);
      alert('Error saving annual discount');
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleToggleDiscountVisibility = async () => {
    try {
      const newVisibility = !annualDiscount.isVisible;
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/membership/admin/settings/annualDiscount`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: annualDiscount.value,
          isVisible: newVisibility,
          description: 'Annual billing discount percentage'
        })
      });
      
      if (response.ok) {
        setAnnualDiscount(prev => ({ ...prev, isVisible: newVisibility }));
      }
    } catch (error) {
      console.error('Error toggling discount visibility:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/membership/admin/plans`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(data.length > 0 ? data : getDefaultPlans());
      } else {
        setPlans(getDefaultPlans());
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlans(getDefaultPlans());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPlans = () => [
    {
      id: 1,
      name: 'basic',
      displayName: 'Basic',
      tagline: 'For Exploring the platform',
      monthlyPrice: 0,
      annualPrice: 0,
      productPostingLimit: 10,
      receiveRFQs: 'Global',
      viewBuyerContacts: 'cross',
      receiveDirectCalls: 'cross',
      buyerDirectoryAccess: 'cross',
      uploadVideos: 'cross',
      maxProductImages: 5,
      companyProfileLogo: 'check',
      liveChat: 'cross',
      highlightBrand: 'cross',
      supplierDashboard: 'check',
      addProfessional: 'cross',
      submitProjects: 'cross',
      postNews: 'cross',
      trustedBadge: 'cross',
      isCurrent: true
    },
    {
      id: 2,
      name: 'gcc-business',
      displayName: 'GCC Business',
      tagline: 'for Gulf-based companies',
      monthlyPrice: 49,
      annualPrice: 349,
      originalPrice: 49,
      savings: 250,
      productPostingLimit: 100,
      receiveRFQs: 'Only GCC',
      viewBuyerContacts: '(GCC Buyers)',
      receiveDirectCalls: 'check',
      buyerDirectoryAccess: '(GCC Buyers)',
      uploadVideos: 'check',
      maxProductImages: 5,
      companyProfileLogo: 'check',
      liveChat: 'check',
      highlightBrand: 'check',
      supplierDashboard: 'check',
      addProfessional: 'check',
      submitProjects: 'check',
      postNews: 'check',
      trustedBadge: 'Available with Extra Fee',
      isCurrent: false
    },
    {
      id: 3,
      name: 'global-enterprise',
      displayName: 'Global Enterprise',
      tagline: 'For Global companies (Gulf included)',
      monthlyPrice: 99,
      annualPrice: 699,
      originalPrice: 149,
      savings: 250,
      productPostingLimit: 200,
      receiveRFQs: 'Global',
      viewBuyerContacts: '(All Buyers)',
      receiveDirectCalls: 'check',
      buyerDirectoryAccess: '(All Buyers)',
      uploadVideos: 'check',
      maxProductImages: 5,
      companyProfileLogo: 'check',
      liveChat: 'check',
      highlightBrand: 'check',
      supplierDashboard: 'check',
      addProfessional: 'check',
      submitProjects: 'check',
      postNews: 'check',
      trustedBadge: 'Available with Extra Fee',
      isCurrent: false
    }
  ];

  const handleCreatePlan = () => {
    navigate('/admin/membership/create');
  };

  const handleEditPlan = (plan) => {
    navigate(`/admin/membership/edit/${plan.id}`);
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/membership/admin/plans/${planId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setPlans(plans.filter(p => p.id !== planId));
        alert('Plan deleted successfully!');
      } else {
        alert('Failed to delete plan');
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Error deleting plan');
    }
  };

  // Feature rows using the same logic as Seller Membership page (source of truth)
  const featureRows = [
    { 
      name: 'Product / Service Posting Limit', 
      type: 'number',
      getValue: (plan) => plan.productPostingLimit?.toString() || '10'
    },
    { 
      name: 'Receive Buy Leads / RFQs',
      getValue: (plan) => {
        // Handle both API format (canReceiveRFQs) and legacy format (receiveRFQs)
        const canReceive = plan.canReceiveRFQs !== undefined ? plan.canReceiveRFQs : (plan.receiveRFQs && plan.receiveRFQs !== 'cross');
        if (!canReceive) return { type: 'x' };
        const region = plan.rfqRegion || (plan.receiveRFQs === 'Only GCC' ? 'gcc' : 'global');
        return { type: 'check', text: region === 'gcc' ? 'Only GCC' : 'Global' };
      }
    },
    { 
      name: 'View Buyer Contact Details',
      getValue: (plan) => {
        const canView = plan.canViewBuyerContacts !== undefined ? plan.canViewBuyerContacts : (plan.viewBuyerContacts && plan.viewBuyerContacts !== 'cross');
        if (!canView) return { type: 'x' };
        const region = plan.buyerContactRegion || (plan.viewBuyerContacts?.includes('GCC') ? 'gcc' : 'all');
        return { type: 'check', text: region === 'gcc' ? '(GCC Buyers)' : '(All Buyers)' };
      }
    },
    { 
      name: 'Receive Direct Calls from Buyers',
      getValue: (plan) => {
        const canReceive = plan.canReceiveDirectCalls !== undefined ? plan.canReceiveDirectCalls : (plan.receiveDirectCalls === 'check');
        return canReceive ? { type: 'check' } : { type: 'x' };
      }
    },
    { 
      name: 'Buyer Directory Access',
      getValue: (plan) => {
        const hasAccess = plan.buyerDirectoryAccess !== undefined ? plan.buyerDirectoryAccess : (plan.buyerDirectoryAccess && plan.buyerDirectoryAccess !== 'cross');
        if (!hasAccess) return { type: 'x' };
        const region = plan.buyerDirectoryRegion || (typeof plan.buyerDirectoryAccess === 'string' && plan.buyerDirectoryAccess.includes('GCC') ? 'gcc' : 'all');
        return { type: 'check', text: region === 'gcc' ? '(GCC Buyers)' : '(All Buyers)' };
      }
    },
    { 
      name: 'Upload Videos in Product Images',
      getValue: (plan) => {
        const canUpload = plan.canUploadVideos !== undefined ? plan.canUploadVideos : (plan.uploadVideos === 'check');
        return canUpload ? { type: 'check' } : { type: 'x' };
      }
    },
    { 
      name: 'Max Product Images per Listing', 
      type: 'number',
      getValue: (plan) => plan.maxProductImages?.toString() || '5'
    },
    { 
      name: 'Company Profile with Logo',
      getValue: (plan) => {
        const hasProfile = plan.hasCompanyProfile !== undefined ? plan.hasCompanyProfile : (plan.companyProfileLogo === 'check');
        return hasProfile ? { type: 'check' } : { type: 'x' };
      }
    },
    { 
      name: 'Live Chat with Buyers',
      getValue: (plan) => {
        const canChat = plan.canLiveChat !== undefined ? plan.canLiveChat : (plan.liveChat === 'check');
        return canChat ? { type: 'check' } : { type: 'x' };
      }
    },
    { 
      name: 'Highlight Your Brand (Optional)',
      getValue: (plan) => {
        const canHighlight = plan.canHighlightBrand !== undefined ? plan.canHighlightBrand : (plan.highlightBrand === 'check');
        return canHighlight ? { type: 'check' } : { type: 'x' };
      }
    },
    { 
      name: 'Supplier Dashboard Access',
      getValue: (plan) => {
        const hasAccess = plan.hasDashboardAccess !== undefined ? plan.hasDashboardAccess : (plan.supplierDashboard === 'check');
        return hasAccess ? { type: 'check' } : { type: 'x' };
      }
    },
    { 
      name: 'Add Profile to Professionals Section',
      getValue: (plan) => {
        const canAdd = plan.canAddProfessional !== undefined ? plan.canAddProfessional : (plan.addProfessional === 'check');
        return canAdd ? { type: 'check' } : { type: 'x' };
      }
    },
    { 
      name: 'Submit Projects in Projects Section',
      getValue: (plan) => {
        const canSubmit = plan.canSubmitProjects !== undefined ? plan.canSubmitProjects : (plan.submitProjects === 'check');
        return canSubmit ? { type: 'check' } : { type: 'x' };
      }
    },
    { 
      name: 'Post News in News Section',
      getValue: (plan) => {
        const canPost = plan.canPostNews !== undefined ? plan.canPostNews : (plan.postNews === 'check');
        return canPost ? { type: 'check' } : { type: 'x' };
      }
    },
    { 
      name: 'Trusted Supplier Badge (Add-on)',
      getValue: (plan) => {
        const available = plan.trustedBadgeAvailable !== undefined ? plan.trustedBadgeAvailable : (plan.trustedBadge && plan.trustedBadge !== 'cross');
        if (!available) return { type: 'x' };
        return { type: 'text', text: 'Available with Extra Fee' };
      }
    },
  ];

  const CheckIcon = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect width="22" height="22" rx="4" fill="#009D0A"/>
      <path d="M16 7L9.5 14L6 10.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CrossIcon = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect width="22" height="22" rx="4" fill="#EF4444"/>
      <path d="M15 7L7 15M7 7L15 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Render feature value - matches Seller Membership page logic
  const renderFeatureValue = (value, type) => {
    // Handle number type (for limits)
    if (type === 'number') {
      return <span className="amp-cell-number">{value}</span>;
    }
    
    // Handle string values (for number results from getValue)
    if (typeof value === 'string') {
      return <span className="amp-cell-number">{value}</span>;
    }
    
    // Handle object values from getValue functions
    if (value && typeof value === 'object') {
      if (value.type === 'check') {
        return (
          <div className="amp-cell-with-text">
            <CheckIcon />
            {value.text && <span className="amp-cell-subtext">{value.text}</span>}
          </div>
        );
      }
      if (value.type === 'x') {
        return <CrossIcon />;
      }
      if (value.type === 'text') {
        return <span className="amp-cell-subtext amp-cell-small">{value.text}</span>;
      }
    }
    
    // Legacy format support
    if (value === 'check') {
      return <CheckIcon />;
    }
    if (value === 'cross') {
      return <CrossIcon />;
    }
    // Text value with optional check (legacy)
    if (value && value !== 'cross' && value !== 'check') {
      return (
        <div className="amp-cell-with-text">
          <CheckIcon />
          <span className="amp-cell-subtext">{value}</span>
        </div>
      );
    }
    return <CrossIcon />;
  };

  const getPrice = (plan) => {
    if (plan.monthlyPrice === 0) return 'Free';
    const price = billingPeriod === 'annual' ? plan.annualPrice : plan.monthlyPrice;
    return `$${price}`;
  };

  const getPeriod = (plan) => {
    if (plan.monthlyPrice === 0) return '';
    return billingPeriod === 'annual' ? '/year' : '/month';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="amp-container">
          <div className="amp-loading">Loading membership plans...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="amp-container">
        {/* Header */}
        <div className="amp-header">
          <div className="amp-header-left">
            <h1 className="amp-title">Membership Packages</h1>
            <p className="amp-subtitle">Flexible plans designed for suppliers, buyers, and professionals at every level.</p>
          </div>
          <button className="amp-create-btn" onClick={handleCreatePlan}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 4V16M4 10H16" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Create New Plan
          </button>
        </div>

        {/* Plans Section */}
        <div className="amp-plans-section">
          {/* Billing Toggle */}
          <div className="amp-billing-wrapper">
          <div className="amp-billing-toggle">
            <span className={`amp-billing-label ${billingPeriod === 'monthly' ? 'active' : ''}`}>Monthly</span>
            <button 
              className={`amp-toggle-switch ${billingPeriod === 'annual' ? 'annual' : ''}`}
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
            >
              <span className="amp-toggle-knob"></span>
            </button>
            <span className={`amp-billing-label ${billingPeriod === 'annual' ? 'active' : ''}`}>Annual</span>
            <span 
              className="amp-discount-badge" 
              onClick={handleOpenDiscountModal}
              title="Click to edit discount"
            >
              {annualDiscount.value}% off
            </span>
          </div>
        </div>

        {/* Plans Row */}
        <div className="amp-plans-row">
          {plans.map((plan) => (
            <div key={plan.id} className={`amp-plan-card ${plan.isCurrent ? 'current' : ''}`}>
              {/* 3-dot menu */}
              <div className="amp-plan-menu amp-plan-card-menu">
                <button 
                  className="amp-menu-dots"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(openMenuId === plan.id ? null : plan.id);
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="4.5" r="2" fill="#6D6D6D"/>
                    <circle cx="12" cy="12" r="2" fill="#6D6D6D"/>
                    <circle cx="12" cy="19.5" r="2" fill="#6D6D6D"/>
                  </svg>
                </button>
                {openMenuId === plan.id && (
                  <div className="amp-menu-dropdown">
                    <button onClick={() => { handleEditPlan(plan); setOpenMenuId(null); }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M10.5 1.75L12.25 3.5M1.75 12.25L2.5 9.25L10 1.75L12.25 4L4.75 11.5L1.75 12.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Edit
                    </button>
                    <button onClick={() => { handleDeletePlan(plan.id); setOpenMenuId(null); }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M1.75 3.5H12.25M5.25 6.125V10.375M8.75 6.125V10.375M2.625 3.5L3.5 11.375C3.5 11.8723 3.9027 12.25 4.375 12.25H9.625C10.0973 12.25 10.5 11.8723 10.5 11.375L11.375 3.5M5.25 3.5V2.625C5.25 2.14175 5.64175 1.75 6.125 1.75H7.875C8.35825 1.75 8.75 2.14175 8.75 2.625V3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Delete
                    </button>
                  </div>
                )}
              </div>

              {/* Plan Content */}
              <div className="amp-plan-content">
                <div className="amp-plan-info">
                  <div className="amp-plan-name">{plan.displayName}</div>
                  <div className="amp-plan-tagline">{plan.tagline}</div>
                  <div className="amp-plan-price">
                    <span className="amp-price-value">{getPrice(plan)}</span>
                    <span className="amp-price-period">{getPeriod(plan)}</span>
                  </div>
                  {billingPeriod === 'annual' && plan.originalPrice && (
                    <div className="amp-plan-savings">
                      <span className="amp-original-price">${plan.originalPrice}</span>
                      <span className="amp-save-badge">Save ${plan.savings}</span>
                    </div>
                  )}
                </div>
                <button className={`amp-plan-btn ${plan.isCurrent ? 'current' : ''}`}>
                  {plan.isCurrent ? 'Current Plan' : 'Upgrade Plan'}
                </button>
              </div>
            </div>
          ))}
        </div>

        </div>

        {/* Features Table */}
        <div className="amp-features-table">
          {featureRows.map((feature, idx) => (
            <div key={idx} className="amp-feature-row">
              <div className="amp-feature-name">{feature.name}</div>
              <div className="amp-feature-values">
                {plans.map((plan) => (
                  <div key={plan.id} className="amp-feature-value">
                    {renderFeatureValue(feature.getValue(plan), feature.type)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="amp-footer">
          <p className="amp-footer-text">
            Your membership isn't just a plan — it's a <span className="amp-footer-highlight">partnership for smarter trade</span>.
          </p>
        </div>

        {/* Annual Discount Modal */}
        {showDiscountModal && (
          <div className="amp-modal-overlay" onClick={() => setShowDiscountModal(false)}>
            <div className="amp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="amp-modal-header">
                <h2 className="amp-modal-title">Annual Discount</h2>
                <button 
                  className={`amp-visibility-btn ${annualDiscount.isVisible ? 'visible' : 'hidden'}`}
                  onClick={handleToggleDiscountVisibility}
                >
                  {annualDiscount.isVisible ? 'Show' : 'Hide'}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    {annualDiscount.isVisible ? (
                      <>
                        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                      </>
                    ) : (
                      <>
                        <path d="M17.94 17.94C16.23 19.24 14.18 20 12 20C5 20 1 12 1 12C2.24 9.68 4.02 7.73 6.17 6.31M9.88 4.24C10.58 4.08 11.29 4 12 4C19 4 23 12 23 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
              
              <div className="amp-modal-body">
                <label className="amp-modal-label">Annual Off</label>
                <div className="amp-modal-input-group">
                  <input
                    type="text"
                    value={discountFormValue}
                    onChange={(e) => setDiscountFormValue(e.target.value)}
                    placeholder="40"
                    className="amp-modal-input"
                  />
                  <span className="amp-modal-suffix">%</span>
                </div>
              </div>
              
              <div className="amp-modal-footer">
                <button className="amp-modal-cancel" onClick={() => setShowDiscountModal(false)}>
                  Cancel
                </button>
                <button className="amp-modal-save" onClick={handleSaveDiscount} disabled={savingDiscount}>
                  {savingDiscount ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMembershipManagePage;
